import { Head, Link, router } from "@inertiajs/react"
import { Loader2, Plus } from "lucide-react"
import { useState } from "react"

import { formatDate } from "@/components/products/work_orders/status-chip"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface LedgerRow {
  id: number
  category: string
  category_label: string
  amount_cents: number
  occurred_on: string
  description: string | null
  car_id: number | null
  car_mark_number: string | null
  work_order_id: number | null
  work_order_label: string | null
  created_at: string
}

interface Props {
  entries: LedgerRow[]
  ytd_total_cents: number
  by_category: { category: string; label: string; amount_cents: number }[]
  categories: { value: string; label: string }[]
  year: number
  can_edit: boolean
}

/**
 * The ROI ledger — the quarterly number the customer shows their own
 * management.
 *
 * EVERY LINE POINTS AT A RECORD. A car or a work order earned each figure,
 * which is the only reason the total is worth putting in front of a finance
 * team; an aggregate somebody typed would be argued away in one meeting.
 */
export default function SavingsIndex({
  entries,
  ytd_total_cents,
  by_category,
  categories,
  year,
  can_edit,
}: Props) {
  const routes = useOrgRoutes()

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Dashboard", href: routes.dashboardPath() },
    { title: "Savings", href: routes.savingsPath() },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Savings" />

      <div className="space-y-5 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Savings</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              What the program has avoided spending, with the record that earned
              each line.
            </p>
          </div>
          {can_edit && (
            <AddEntryDialog
              categories={categories}
              savingsPath={routes.savingsPath()}
            />
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Card className="col-span-2 lg:col-span-1">
            <CardContent className="pt-6">
              <p className="text-muted-foreground text-xs">{year} to date</p>
              <p className="mt-1 text-3xl font-semibold tabular-nums">
                {money(ytd_total_cents)}
              </p>
            </CardContent>
          </Card>
          {by_category.map((row) => (
            <Card key={row.category}>
              <CardContent className="pt-6">
                <p className="text-muted-foreground text-xs">{row.label}</p>
                <p className="mt-1 text-xl font-semibold tabular-nums">
                  {money(row.amount_cents)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Car</TableHead>
                <TableHead>Work order</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-muted-foreground py-10 text-center text-sm"
                  >
                    Nothing logged yet. Skipping a conflicting requal on the
                    Requal Program page writes the first line here.
                  </TableCell>
                </TableRow>
              )}
              {entries.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="text-sm tabular-nums">
                    {formatDate(row.occurred_on)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{row.category_label}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {row.car_id ? (
                      <Link
                        href={routes.carPath(row.car_id)}
                        className="hover:underline"
                      >
                        {row.car_mark_number}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {row.work_order_id ? (
                      <Link
                        href={routes.workOrderPath(row.work_order_id)}
                        className="hover:underline"
                      >
                        {row.work_order_label}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-96 truncate text-sm">
                    {row.description ?? "—"}
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums">
                    {money(row.amount_cents)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </AppLayout>
  )
}

function AddEntryDialog({
  categories,
  savingsPath,
}: {
  categories: { value: string; label: string }[]
  savingsPath: string
}) {
  const [open, setOpen] = useState(false)
  const [category, setCategory] = useState(categories[0]?.value ?? "other")
  const [amount, setAmount] = useState("")
  const [occurredOn, setOccurredOn] = useState("")
  const [description, setDescription] = useState("")
  const [saving, setSaving] = useState(false)

  function submit() {
    const dollars = Number(amount)
    if (saving || !Number.isFinite(dollars) || dollars <= 0) return
    setSaving(true)
    router.post(
      savingsPath,
      {
        savings_ledger_entry: {
          category,
          amount_cents: Math.round(dollars * 100),
          occurred_on: occurredOn || null,
          description: description || null,
        },
      },
      {
        onSuccess: () => {
          setAmount("")
          setDescription("")
          setOccurredOn("")
          setOpen(false)
        },
        onFinish: () => setSaving(false),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="mr-1 size-4" />
          Log savings
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Log savings</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="savings-category">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="savings-category" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="savings-amount">Amount (USD)</Label>
            <Input
              id="savings-amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="savings-date">Occurred on</Label>
            <Input
              id="savings-date"
              type="date"
              value={occurredOn}
              onChange={(event) => setOccurredOn(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="savings-note">Description</Label>
            <Textarea
              id="savings-note"
              rows={2}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving || !amount}>
            {saving && <Loader2 className="mr-1 size-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`
}
